import { z } from 'zod'

import { ApiClientError } from '@ems/api-client/client'

import { api } from '#admin/lib/api-client.ts'

const PATH = '/v1/admin/tenant/organization'

/**
 * The organizer's legal identity, as printed on invoices. Two logos, as v1 had: the company's
 * mark on the left of the PDF header, the event's on the right. `faviconKey` is not printed: it
 * is the browser tab icon, on the website and here.
 */
export const organizationSchema = z.object({
  organizerName: z.string().nullable(),
  organizerAddress: z.string().nullable(),
  organizerPhone: z.string().nullable(),
  organizerEmail: z.string().nullable(),
  organizerWebsite: z.string().nullable(),
  organizerTaxId: z.string().nullable(),
  organizerLogoKey: z.string().nullable(),
  eventLogoKey: z.string().nullable(),
  // `.default(null)`: the console is deployed ahead of the API, whose answer may not carry it yet.
  faviconKey: z.string().nullable().default(null),
  invoiceFooter: z.string().nullable(),
})

export type Organization = z.infer<typeof organizationSchema>

export function fetchOrganization(signal?: AbortSignal): Promise<Organization> {
  return api.get(PATH, { schema: organizationSchema, ...(signal ? { signal } : {}) })
}

/** The whole object, every time: the endpoint is a PUT, and `null` clears a field. */
export function updateOrganization(body: Organization): Promise<Organization> {
  return api.put(PATH, { schema: organizationSchema, body })
}

/** A short-lived URL for one branding upload — saved, or uploaded and not saved yet. */
export function fetchLogoUrl(key: string, signal?: AbortSignal): Promise<string> {
  return api
    .get(`${PATH}/logo?${new URLSearchParams({ key }).toString()}`, {
      schema: z.object({ url: z.string() }),
      ...(signal ? { signal } : {}),
    })
    .then((response) => response.url)
}

/** What the form edits: every input as the control holds it, `''` for nothing. */
export type OrganizationDraft = { [Field in keyof Organization]: string }

export type OrganizationField = keyof OrganizationDraft

export function draftOf(organization: Organization): OrganizationDraft {
  return {
    organizerName: organization.organizerName ?? '',
    organizerAddress: organization.organizerAddress ?? '',
    organizerPhone: organization.organizerPhone ?? '',
    organizerEmail: organization.organizerEmail ?? '',
    organizerWebsite: organization.organizerWebsite ?? '',
    organizerTaxId: organization.organizerTaxId ?? '',
    organizerLogoKey: organization.organizerLogoKey ?? '',
    eventLogoKey: organization.eventLogoKey ?? '',
    faviconKey: organization.faviconKey ?? '',
    invoiceFooter: organization.invoiceFooter ?? '',
  }
}

const orNull = (value: string) => (value.trim() === '' ? null : value.trim())

/** The wire body. An emptied input is `null`, which clears its column rather than keeping it. */
export function bodyOf(draft: OrganizationDraft): Organization {
  return {
    organizerName: orNull(draft.organizerName),
    organizerAddress: orNull(draft.organizerAddress),
    organizerPhone: orNull(draft.organizerPhone),
    organizerEmail: orNull(draft.organizerEmail),
    organizerWebsite: orNull(draft.organizerWebsite),
    organizerTaxId: orNull(draft.organizerTaxId),
    organizerLogoKey: orNull(draft.organizerLogoKey),
    eventLogoKey: orNull(draft.eventLogoKey),
    faviconKey: orNull(draft.faviconKey),
    invoiceFooter: orNull(draft.invoiceFooter),
  }
}

/** What is wrong with a draft, by field. Empty when it can be sent. The server checks both too. */
export function draftIssues(draft: OrganizationDraft): Partial<Record<OrganizationField, string>> {
  const issues: Partial<Record<OrganizationField, string>> = {}
  const email = draft.organizerEmail.trim()
  const website = draft.organizerWebsite.trim()
  if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    issues.organizerEmail = 'Enter an email address, such as finance@example.com.'
  }
  if (website !== '' && !isWebAddress(website)) {
    issues.organizerWebsite = 'Enter the full address, starting with https://.'
  }
  return issues
}

function isWebAddress(value: string): boolean {
  return URL.canParse(value) && /^https?:$/.test(new URL(value).protocol)
}

/**
 * Which field a refusal belongs beside. The service names it as `detail.field` (a logo key); the
 * schema validator lists `detail.issues`, whose first path names it (a malformed email or
 * website). `undefined` sends it to a toast instead.
 */
export function refusalField(error: unknown): OrganizationField | undefined {
  if (!(error instanceof ApiClientError) || error.code !== 'validation_failed') return undefined
  const detail = error.detail ?? {}
  const issues = detail['issues']
  const named = Array.isArray(issues) ? firstPath(issues[0]) : detail['field']
  return isField(named) ? named : undefined
}

function isField(value: unknown): value is OrganizationField {
  return typeof value === 'string' && Object.hasOwn(organizationSchema.shape, value)
}

function firstPath(issue: unknown): unknown {
  if (typeof issue !== 'object' || issue === null || !('path' in issue)) return undefined
  return Array.isArray(issue.path) ? issue.path[0] : undefined
}
